import React from 'react';

import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PatientActivity } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import { usePatientActivityQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { Loader, useOnScreenObserver } from '@uplift/ui-v2';

import { ActivityPage } from './ActivityPage.component';
import { AddActivityButton } from './AddActivityButton.component';

export type ActivityListProps = {
  patientId: string;
  onActivitySelect?: (item: PatientActivity) => void;
  onUploadDocumentOpen?: () => void;
};

export const ActivityList: React.VFC<ActivityListProps> = ({ patientId, onActivitySelect, onUploadDocumentOpen }) => {
  const { classes } = useStyles();
  const timezone = useTimezone();
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } = usePatientActivityQuery(patientId, { suspense: true });

  const now = React.useMemo(() => CalendarDateService.date().tz(timezone), [ timezone ]);
  const screenObserverRef = useOnScreenObserver(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  });

  const hasSomeActivities = React.useMemo(
    () => data?.pages?.[0]?.data?.length > 0,
    [ data ]
  );

  const content = React.useMemo(() => {
    if (!hasSomeActivities) {
      return (
        <Typography component="i" color="textSecondary">No activity yet.</Typography>
      );
    }

    return data?.pages?.map((page, index, arr) => {
      return page.data.length > 0 && (
        <ActivityPage
          key={page.cursor}
          now={now}
          prevActivities={arr[index - 1]?.data}
          activities={page.data}
          onItemSelect={onActivitySelect}
        />
      );
    });
  }, [ data?.pages, hasSomeActivities, now, onActivitySelect ]);

  return (
    <Box>
      <Box mb={3} display="flex" justifyContent="space-between">
        <Typography variant="h5" className={classes.title}>Activity</Typography>
        <AddActivityButton
          patientId={patientId}
          onUploadDocumentOpen={onUploadDocumentOpen}
        />
      </Box>
      <Box display="flex" flexDirection="column" gap="8px">
        {content}
        {hasNextPage && (
          <div ref={screenObserverRef}>
            <Loader color="secondary" size="2rem" mt={3} />
          </div>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()({
  title: {
    fontWeight: 450
  }
});
